
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import { supabase } from '../lib/supabase';

const AdminPanel = ({ isOpen, setIsOpen }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [newPledgeCount, setNewPledgeCount] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      const { data: settingData } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'pledge_count')
        .single();
      
      if (settingData) {
        setNewPledgeCount(settingData.value);
      }

      const { data: adminData } = await supabase
        .from('admins')
        .select('*')
        .order('created_at', { ascending: false });
      
      setAdmins(adminData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('admins')
        .select('*')
        .eq('email', email)
        .single();

      if (error) throw error;

      if (data) {
        setIsAuthenticated(true);
        toast({
          title: "Success",
          description: "Logged in successfully",
        });
      } else {
        toast({
          title: "Access Denied",
          description: "Invalid admin credentials",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Error",
        description: "Login failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePledgeCount = async () => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key: 'pledge_count', 
          value: newPledgeCount.toString() 
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Pledge count updated successfully",
      });
      
      loadData();
    } catch (error) {
      console.error('Error updating pledge count:', error);
      toast({
        title: "Error",
        description: "Failed to update pledge count",
        variant: "destructive",
      });
    }
  };

  const addAdmin = async () => {
    if (!newAdminEmail) return;

    try {
      const { error } = await supabase
        .from('admins')
        .insert([{ email: newAdminEmail }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Admin added successfully",
      });
      setNewAdminEmail('');
      loadData();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast({
        title: "Error",
        description: "Failed to add admin",
        variant: "destructive",
      });
    }
  };

  const removeAdmin = async (adminEmail) => {
    try {
      const { error } = await supabase
        .from('admins')
        .delete()
        .eq('email', adminEmail);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Admin removed successfully",
      });
      loadData();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast({
        title: "Error",
        description: "Failed to remove admin",
        variant: "destructive",
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center"
          onClick={() => setIsOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="w-full max-w-lg mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 shadow-xl">
              {!isAuthenticated ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <h2 className="text-2xl font-bold text-white mb-4">Admin Login</h2>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter admin email"
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                    required
                  />
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={loading}
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-white">Admin Panel</h2>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-white/60 hover:text-white"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Pledge Count Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Update Global Pledge Count</h3>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={newPledgeCount}
                        onChange={(e) => setNewPledgeCount(e.target.value)}
                        className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                        placeholder="Enter new count"
                      />
                      <Button
                        onClick={updatePledgeCount}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Admins Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Manage Admins</h3>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={newAdminEmail}
                        onChange={(e) => setNewAdminEmail(e.target.value)}
                        className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                        placeholder="Enter admin email"
                      />
                      <Button
                        onClick={addAdmin}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Add
                      </Button>
                    </div>

                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {admins.map((admin) => (
                        <div
                          key={admin.id}
                          className="flex justify-between items-center bg-white/5 rounded-lg p-3"
                        >
                          <span className="text-white">{admin.email}</span>
                          <Button
                            onClick={() => removeAdmin(admin.email)}
                            variant="destructive"
                            size="sm"
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdminPanel;
